import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { accountGetUsers, accountDeleteUser } from '../../api';
import { SmartField_Api_Contracts_Account_UserListItemDto } from '../../api/types.gen';
import { EditUserModal } from '../../components/EditUserModal';
import { useIsAdmin, useAffiliatedSiteIds, useIsUserManager, useAuth } from '../../lib/auth';
import { SmartFieldClaimType } from '../../lib/auth/types';

// Site-related claim types that have site IDs as values
const SITE_CLAIM_TYPES = new Set([
  SmartFieldClaimType.SiteManager,
  SmartFieldClaimType.UserManager,
  SmartFieldClaimType.RawDataExporter,
  SmartFieldClaimType.RawDataReader,
  SmartFieldClaimType.RawDataEditor,
  SmartFieldClaimType.FluxDataExporter,
  SmartFieldClaimType.FluxDataReader,
  SmartFieldClaimType.FluxDataEditor,
  SmartFieldClaimType.TrialManager,
]);

export function UsersPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<SmartField_Api_Contracts_Account_UserListItemDto | null>(null);
  const [deletingUser, setDeletingUser] = useState<SmartField_Api_Contracts_Account_UserListItemDto | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isAdmin = useIsAdmin();
  const isUserManager = useIsUserManager();
  const userSiteIds = useAffiliatedSiteIds();
  const { user: currentUser } = useAuth();

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => accountGetUsers(),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => accountDeleteUser({ userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeletingUser(null);
    },
  });

  const filteredUsers = users?.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  const getUserRoles = (user: SmartField_Api_Contracts_Account_UserListItemDto): string[] => {
    const roles = user.claims
      .filter(c => c.type === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role')
      .map(c => c.value || '');
    return roles.length > 0 ? roles : ['User'];
  };

  const getUserSiteCount = (user: SmartField_Api_Contracts_Account_UserListItemDto): number => {
    // Count unique site IDs from claims
    const siteIds = new Set(
      user.claims
        .filter(c => SITE_CLAIM_TYPES.has(c.type as SmartFieldClaimType))
        .map(c => c.value)
        .filter(v => v && !isNaN(Number(v)))
    );
    return siteIds.size;
  };

  const canEditUser = (user: SmartField_Api_Contracts_Account_UserListItemDto): boolean => {
    if (isAdmin) return true;
    
    // UserManagers can only edit users whose site claims overlap with their own sites
    const targetUserSiteIds = new Set(
      user.claims
        .filter(c => SITE_CLAIM_TYPES.has(c.type as SmartFieldClaimType) && !isNaN(Number(c.value)))
        .map(c => parseInt(c.value, 10))
    );
    
    // If target user has no site claims, user manager cannot edit
    if (targetUserSiteIds.size === 0) return false;
    
    // Check if all target user's sites are within user manager's sites
    return Array.from(targetUserSiteIds).every(siteId => userSiteIds.includes(siteId));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-smartfield mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-red-900">Error Loading Users</h3>
              <p className="mt-1 text-sm text-red-700">{String(error)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                {filteredUsers?.length || 0} {filteredUsers?.length === 1 ? 'user' : 'users'}
              </span>
              {(isAdmin || isUserManager) && (
                <button
                  onClick={() => navigate('/account/register')}
                  className="px-4 py-2 bg-smartfield hover:bg-smartfield-700 text-white font-medium rounded-lg shadow transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Register User
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by email or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
            />
          </div>
        </div>

        {/* User Table */}
        {filteredUsers && filteredUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Roles
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sites
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-smartfield text-white flex items-center justify-center font-semibold">
                          {user.email.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.userName || 'No username'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.emailConfirmed ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Confirmed
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {getUserRoles(user).map((role, idx) => (
                          <span
                            key={idx}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              role === 'Admin'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {getUserSiteCount(user)} {getUserSiteCount(user) === 1 ? 'site' : 'sites'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {canEditUser(user) ? (
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => setEditingUser(user)}
                            className="text-smartfield hover:text-smartfield-700 font-medium"
                          >
                            Edit Claims
                          </button>
                          {user.email !== currentUser?.email && (
                            <button
                              onClick={() => setDeletingUser(user)}
                              className="text-red-600 hover:text-red-700 font-medium"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">No permission</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search terms.' : 'No users available.'}
            </p>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          isOpen={true}
          onClose={() => setEditingUser(null)}
        />
      )}

      {/* Delete Confirmation */}
      {deletingUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setDeletingUser(null)}
              aria-hidden="true"
            />
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full p-6">
              <h3 className="text-lg font-medium text-gray-900">Delete user</h3>
              <p className="mt-2 text-sm text-gray-600">
                Are you sure you want to delete <span className="font-medium">{deletingUser.email}</span>? This cannot be undone.
              </p>
              {deleteMutation.error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">{String(deleteMutation.error)}</p>
                </div>
              )}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDeletingUser(null)}
                  className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(deletingUser.id)}
                  className="px-4 py-2 rounded-md border border-transparent bg-red-600 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
